import { useTranslation, Trans } from 'react-i18next';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Seo from '../components/Seo';
import { PhoneCall, Loader2, CheckCircle2, AlertCircle, PhoneForwarded, Clock } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { ZahlartWahl } from '../components/zahlung/ZahlartWahl';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (c) => `${(c / 100).toFixed(2).replace('.', ',')} €`;

const StatusBox = ({ tone, icon: Icon, title, children, testid }) => {
  const tones = { ok: 'bg-green-50 border-green-600 text-green-800', warn: 'bg-[#fff8e1] border-[#b8893d] text-[#8a6d1a]', err: 'bg-red-50 border-red-500 text-red-700', info: 'bg-[#f5f8fa] border-[#1B3A5C] text-[#1B3A5C]' };
  return (
    <div className={`border-l-4 p-4 text-sm leading-relaxed ${tones[tone]}`} data-testid={testid}>
      <div className="flex items-start gap-2"><Icon className="w-4 h-4 mt-0.5 flex-shrink-0" /><div><strong>{title}</strong>{children && <div className="mt-1">{children}</div>}</div></div>
    </div>
  );
};

const ConnectPanel = ({ nf, keyParam, access, onDone }) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/notfall/${nf}/connect`, { key: keyParam });
      setResult(data);
      onDone();
    } catch (e) {
      toast({ title: t('notfallkontakt.c_error'), description: e?.response?.data?.detail || t('notfallkontakt.c_retry'), variant: 'destructive' });
    } finally { setBusy(false); }
  };
  if (result?.mode === 'fallback') {
    return (
      <div className="text-center" data-testid="connect-fallback">
        <p className="text-[#34536b] mb-4">{t('notfallkontakt.c_call_now')}</p>
        <a href={`tel:${result.hotline.tel}`} data-testid="notfall-hotline-call" className="inline-flex items-center gap-3 bg-[#c0392b] hover:bg-[#a93226] text-white rounded-sm font-semibold px-10 py-5 text-2xl md:text-3xl"><PhoneCall className="w-8 h-8" /> {result.hotline.display}</a>
      </div>
    );
  }
  if (result?.mode === 'twilio') {
    return <StatusBox tone="ok" icon={PhoneForwarded} title={t('notfallkontakt.c_calling')} testid="connect-calling">{t('notfallkontakt.c_calling_text', { phone: access.phone_masked, n: Math.max(0, access.connects_left - 1) })}</StatusBox>;
  }
  return (
    <div className="text-center">
      <p className="text-[#34536b] mb-5"><Trans i18nKey="notfallkontakt.c_intro" values={{ phone: access.phone_masked }} components={{ b: <strong /> }} /></p>
      <button type="button" onClick={connect} disabled={busy} data-testid="notfall-connect" className="inline-flex items-center gap-3 bg-[#c0392b] hover:bg-[#a93226] disabled:opacity-60 text-white rounded-sm font-semibold px-10 py-5 text-xl md:text-2xl tracking-wide">
        {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <PhoneForwarded className="w-6 h-6" />} {t('notfallkontakt.c_cta')}
      </button>
      <p className="text-xs text-[#7a8a99] mt-3">{t('notfallkontakt.c_left', { n: access.connects_left })}</p>
    </div>
  );
};

const PaymentPanel = ({ nf, keyParam, access, onPaid }) => {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const [busy, setBusy] = useState('');
  const amount = access.payments.amount_cents;
  const stripe = async (zahlart) => {
    setBusy(zahlart);
    try {
      const { data } = await axios.post(`${API}/notfall/${nf}/pay/stripe`, { key: keyParam, origin_url: window.location.origin, zahlart });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast({ title: t('notfallkontakt.p_error'), description: e?.response?.data?.detail || t('notfallkontakt.c_retry'), variant: 'destructive' });
      setBusy(false);
    }
  };
  return (
    <div data-testid="payment-panel">
      <div className="bg-[#fbf7f0] border-l-4 border-[#b8893d] p-5 mb-6">
        <div className="text-[11px] tracking-[0.28em] text-[#b8893d] uppercase mb-1">{t('notfallkontakt.p_pauschale')}</div>
        <div className="font-serif text-3xl text-[#1B3A5C]" data-testid="payment-amount">{eur(amount)} <span className="text-sm font-sans text-[#7a8a99]">{t('notfallkontakt.p_brutto')}</span></div>
        <p className="text-xs text-[#34536b] mt-2">{t('notfallkontakt.p_text')}</p>
      </div>
      <div data-testid="pay-stripe"><ZahlartWahl betrag={amount / 100} busy={busy} onWahl={stripe} testId="notfall-zahlart" /></div>
      <div className="my-4 flex items-center gap-3 text-xs text-[#9aa5b1]"><span className="flex-1 h-px bg-gray-200" />{t('notfallkontakt.p_or')}<span className="flex-1 h-px bg-gray-200" /></div>
      {access.payments.paypal_client_id ? (
        <PayPalScriptProvider options={{ clientId: access.payments.paypal_client_id, currency: 'EUR', intent: 'capture', locale: { de: 'de_DE', en: 'en_US', fr: 'fr_FR', it: 'it_IT', tr: 'tr_TR', ar: 'ar_EG' }[(i18n.language || 'de').slice(0, 2)] || 'de_DE' }}>
          <div data-testid="pay-paypal">
            <PayPalButtons style={{ layout: 'horizontal', color: 'gold', tagline: false, height: 48 }}
              createOrder={async () => (await axios.post(`${API}/notfall/${nf}/pay/paypal/create`, { key: keyParam })).data.order_id}
              onApprove={async (d) => { await axios.post(`${API}/notfall/${nf}/pay/paypal/capture`, { key: keyParam, order_id: d.orderID }); onPaid(); }}
              onError={() => toast({ title: t('notfallkontakt.p_paypal_failed'), variant: 'destructive' })} />
          </div>
        </PayPalScriptProvider>
      ) : (
        <button type="button" disabled data-testid="pay-paypal-disabled" className="w-full border border-gray-200 text-[#9aa5b1] rounded-sm px-6 py-3 text-xs tracking-[0.15em] cursor-not-allowed">{t('notfallkontakt.p_paypal_soon')}</button>
      )}
    </div>
  );
};

const NotfallZugang = () => {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const nf = params.get('nf');
  const keyParam = params.get('key') || '';
  const sessionId = params.get('session_id');
  const cancelled = params.get('cancelled');
  const [access, setAccess] = useState(null);
  const [error, setError] = useState(null);
  const [polling, setPolling] = useState(Boolean(sessionId));
  const tries = useRef(0);

  const load = useCallback(async () => {
    try { setAccess((await axios.get(`${API}/notfall/${nf}/access`, { params: { key: keyParam || '' } })).data); }
    catch (e) { setError(e?.response?.data?.detail || t('notfallkontakt.z_load_error')); }
  }, [nf, t]);

  useEffect(() => { if (nf) load(); else setError(t('notfallkontakt.z_invalid')); }, [nf, load, t]);

  useEffect(() => {
    if (!sessionId) return undefined;
    let timer;
    const poll = async () => {
      tries.current += 1;
      try {
        const { data } = await axios.get(`${API}/notfall/pay/status/${sessionId}`);
        if (data.payment_status === 'paid') { setPolling(false); load(); return; }
        if (['expired', 'failed'].includes(data.payment_status) || tries.current > 8) { setPolling(false); return; }
      } catch (e) { if (tries.current > 8) { setPolling(false); return; } }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, load]);

  const behoerde = access?.modus === 'behoerde';
  return (
    <div className="min-h-screen bg-white">
      <Seo title={t('notfallkontakt.z_seo_title')} description={t('notfallkontakt.z_seo_desc')} path="/notfallkontakt/zugang" noindex />
      <Header />
      <main className="pt-[150px] pb-20">
        <section className="bg-[#1B3A5C] text-white py-12">
          <div className="max-w-3xl mx-auto px-6 lg:px-10">
            <div className="flex items-center gap-3 text-[#e0b56a] text-xs tracking-[0.3em] mb-3"><span className="w-12 h-px bg-[#b8893d]"></span>{t('notfallkontakt.eyebrow')}</div>
            <h1 className="font-serif font-light text-3xl md:text-4xl tracking-tight">{t(access?.requires_payment ? 'notfallkontakt.z_title_pay' : 'notfallkontakt.z_title_connect')}</h1>
            {access && <p className="text-white/70 text-sm mt-2" data-testid="zugang-meta">{access.name} · {access.notfallArt} · {t('notfallkontakt.z_id')} <strong className="text-white">{nf}</strong></p>}
          </div>
        </section>
        <section className="max-w-3xl mx-auto px-6 lg:px-10 mt-10 space-y-6">
          {error && <StatusBox tone="err" icon={AlertCircle} title={error} testid="zugang-error"><Link to="/notfallkontakt" className="underline">{t('notfallkontakt.z_back')}</Link></StatusBox>}
          {!error && !access && <div className="flex items-center gap-2 text-sm text-[#34536b]"><Loader2 className="w-4 h-4 animate-spin" /> {t('notfallkontakt.z_loading')}</div>}
          {polling && <StatusBox tone="info" icon={Loader2} title={t('notfallkontakt.z_polling')} testid="payment-polling">{t('notfallkontakt.z_polling_text')}</StatusBox>}
          {cancelled && access?.requires_payment && !polling && <StatusBox tone="warn" icon={AlertCircle} title={t('notfallkontakt.z_cancelled')} testid="payment-cancelled">{t('notfallkontakt.z_cancelled_text')}</StatusBox>}
          {access && !polling && (
            access.requires_payment
              ? <PaymentPanel nf={nf} keyParam={keyParam} access={access} onPaid={load} />
              : access.access_expired
                ? <StatusBox tone="warn" icon={Clock} title={t('notfallkontakt.z_expired')} testid="access-expired">{t('notfallkontakt.z_expired_text', { tel: '+49 228 30 40 640' })}</StatusBox>
                : (
                  <div className="space-y-6">
                    {behoerde
                      ? <StatusBox tone="ok" icon={CheckCircle2} title={t('notfallkontakt.z_free')} testid="access-free" />
                      : <StatusBox tone="ok" icon={CheckCircle2} title={t('notfallkontakt.z_paid')} testid="access-paid">{t('notfallkontakt.z_paid_text')}</StatusBox>}
                    {access.can_connect
                      ? <ConnectPanel nf={nf} keyParam={keyParam} access={access} onDone={load} />
                      : <StatusBox tone="warn" icon={AlertCircle} title={t('notfallkontakt.z_exhausted')} testid="access-exhausted">{t('notfallkontakt.z_exhausted_text', { tel: '+49 228 30 40 640' })}</StatusBox>}
                  </div>
                )
          )}
        </section>
      </main>
      <Footer />
      <Toaster />
    </div>
  );
};

export default NotfallZugang;
